import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import StorageBar from '../../components/StorageBar/StorageBar';
import {
  updateFlashBar,
  updateIsLoading,
  update_breadcrumb,
} from '../../redux/action';
import PageHeading from '../../components/PageHeading/PageHeading';
import Button from '../../components/Button/Button';
import InputForm from '../../components/InputForm/InputForm';
import Input from '../../components/Input/Input';
import Dropdown from '../../components/Dropdown/Dropdown';
import { DriveType } from '../../config/enum';
import instance from '../../service/axiosInstane';
import { flashBarTheme } from '../../config/flashBar';
import { errorMessage } from '../../config/errorsMsg';
import { errorMessageAutoCapitalize } from '../../config/helpers';
import { keyword } from '../../config/keyword';
import { colors } from '../../config/constants';

const driveTypeOptions = [
  { name: keyword.EXTERNAL_STORAGE, id: DriveType.External },
  { name: keyword.LOCAL_STORAGE, id: DriveType.Local },
];

const Server = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [formError, setFormError] = useState<any>({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [storage, setStorage] = useState<any>([]);
  const [state, setState] = useState<any>({
    type: '',
    drivePath: '',
    totalMemory: null,
    totalMemorytype: '',
    code: '',
  });
  const { type, drivePath, code, totalMemory, totalMemorytype } = state;
  const [appLog, setAppLog] = useState([
    {
      fileName: '',
      date: '',
    },
  ]);

  useEffect(() => {
    const breadcrumb = [{ path: '/server', name: 'Server' }];
    dispatch(update_breadcrumb(breadcrumb));
    getApplicationLogDetails();
    getAllStorage();
  }, []);

  useEffect(() => {
    if (Object.keys(formError).length === 0 && isSubmit) {
      addNewStorage();
    }
  }, [formError]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (event.target.name === 'code') {
      const format = /^\S*$/;
      format.test(event.target.value) &&
        setState({
          ...state,
          [event.target.name]: event.target.value,
        });
    } else {
      setState({ ...state, [event.target.name]: event.target.value });
    }
    if (event.target.value !== '' || Number(event.target.value) !== 0) {
      setFormError({ ...formError, [event.target.name]: undefined });
    } else {
      setFormError({
        ...formError,
        [event.target.name]: `${errorMessageAutoCapitalize(
          event.target.name
        )} is required`,
      });
    }
  };

  const onSubmit = () => {
    setFormError(validation());
    setIsSubmit(true);
  };

  const validation = () => {
    const error: any = {};
    if (!code) {
      error.code = errorMessage.emptyDriveCode;
    } else if (code.length < 1 && code.length > 16) {
      error.code = errorMessage.drivePathLength;
    }
    if (!drivePath) {
      error.drivePath = errorMessage.emptyDrivePath;
    } else if (drivePath.length > 255) {
      error.drivePath = errorMessage.drivePathLength;
    }
    if (!totalMemory) {
      error.totalMemory = errorMessage.emptyTotalMemory;
    } else if (totalMemory < 0) {
      error.totalMemory = errorMessage.negativeTotalMemory;
    }
    if (!totalMemorytype) {
      error.totalMemorytype = errorMessage.emptyTotalMemoryType;
    }
    if (!type) {
      error.type = errorMessage.emptyDriveType;
    }

    return error;
  };

  const getAllStorage = async () => {
    dispatch(updateIsLoading(true));
    await instance
      .get('/filestore-drives')
      .then((response) => {
        if (response.status === 200) {
          gotStorageResponse(response.data);
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        dispatch(updateIsLoading(false));
      });
  };

  const addNewStorage = async () => {
    const obj = {
      type: Number(type),
      drivePath: drivePath,
      code: code,
      totalMemory: bytesConverter(totalMemory, totalMemorytype),
    };
    dispatch(updateIsLoading(true));
    await instance
      .post('/filestore-drives', obj)
      .then((response) => {
        if (response.status === 201) {
          const obj = {
            isOpen: true,
            message: response.data.message,
            type: flashBarTheme.SUCCESS,
          };
          dispatch(updateFlashBar(obj));
          setIsFormOpen(false);
          getAllStorage();
          setState({
            ...state,
            type: '',
            drivePath: '',
            code: '',
            totalMemory: 0,
            totalMemorytype: '',
          });
          dispatch(updateIsLoading(false));
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message || err.response.data.errors.Code[0],
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        dispatch(updateIsLoading(false));
      });
  };

  const getApplicationLogDetails = () => {
    dispatch(updateIsLoading(true));
    instance
      .get('/app/logfiles')
      .then((response) => {
        if (response.status === 200) {
          gotResponse(response.data);
          dispatch(updateIsLoading(false));
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        dispatch(updateIsLoading(false));
      });
  };

  const gotStorageResponse = (data: any) => {
    setStorage(data);
  };

  const gotResponse = (data: any) => {
    setAppLog(data);
    dispatch(updateIsLoading(false));
  };

  const onCancelHandler = () => {
    setIsFormOpen(false);
    setFormError({});
    setIsSubmit(false);
    setState({
      ...state,
      type: '',
      drivePath: '',
      code: '',
      totalMemory: 0,
      totalMemorytype: '',
    });
  };

  const bytesConverter = (totalMemory: number, totalMemorytype: string) => {
    switch (totalMemorytype) {
      case 'KB':
        return totalMemory * 1024;
      case 'MB':
        return totalMemory * 1024 * 1024;
      case 'GB':
        return totalMemory * 1024 * 1024 * 1024;
      case 'TB':
        return totalMemory * 1024 * 1024 * 1024 * 1024;
    }
  };
  const currentDate = (item: string) => {
    const currentDate = new Date(); // Get the current date

    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(currentDate.getDate()).padStart(2, '0');

    const formattedDate = `${year}-${month}-${day}`;
    if (item === formattedDate) {
      return true;
    } else {
      return false;
    }
  };
  return (
    <>
      <div className="px-2 ">
        <div className="flex justify-between items-center">
          <div className="w-full">
            <PageHeading label="Storage Management" />
          </div>
        </div>

        <div className="p-4 bg-white rounded-sm shadow-lg">
          <div className="sm:w-full lg:flex items-center justify-end">
            <Button
              label="Add"
              type="button"
              buttonType="primary"
              icon={<i className="fa-solid fa-plus"></i>}
              onClick={() => setIsFormOpen(!isFormOpen)}
            />
          </div>
          <InputForm
            title={'Add New Storage'}
            isFormOpen={isFormOpen}
            onSubmit={onSubmit}
            onCancel={() => onCancelHandler()}
          >
            <>
              <div className="lg:flex items-center justify-between">
                <Input
                  name={'code'}
                  value={code}
                  type={'text'}
                  label={'Code'}
                  placeholder={`Enter code`}
                  onChange={handleChange}
                  error={formError?.code}
                />
                <Input
                  value={drivePath}
                  name={'drivePath'}
                  type={'text'}
                  label={'Drive Path'}
                  placeholder={`Enter drive path`}
                  onChange={handleChange}
                  error={formError?.drivePath}
                />
              </div>
              <div className="lg:flex items-center justify-between">
                <div className="lg:flex flex-col justify-between">
                  <label
                    className={`${colors.primaryText} ml-2 mb-1 text-sm font-semibold`}
                  >
                    Total memory
                  </label>
                  <div className="flex">
                    <input
                      className={`w-full xl:w-[200px] h-7 outline-none pl-2 text-sm border-b-2 focus:border-[#0369a1] ${
                        formError.totalMemory === undefined ? '' : 'border-[red]'
                      } placeholder-[#808080]`}
                      value={totalMemory}
                      name={'totalMemory'}
                      type={'number'}
                      placeholder={`Enter total memory`}
                      onChange={handleChange}
                    />
                    <select
                      className={`w-full lg:w-[100px] ml-2 xl:w-[100px] h-7 outline-none pl-2 text-sm border-b-2 focus:border-[#0369a1] ${
                        formError.totalMemory === undefined ? '' : 'border-[red]'
                      } placeholder-[#808080]`}
                      onChange={handleChange}
                      value={totalMemorytype}
                      name="totalMemorytype"
                    >
                      <option value="">Select size</option>
                      <option value="KB">KB</option>
                      <option value="MB">MB</option>
                      <option value="GB">GB</option>
                      <option value="TB">TB</option>
                    </select>
                  </div>
                  <div className="">
                    <div className="text-sm text-[red] ml-2 mt-1">
                      {formError.totalMemory}
                    </div>
                    <div className="text-sm text-[red] ml-2 mt-1">
                      {formError.totalMemorytype}
                    </div>
                  </div>
                </div>
                <Dropdown
                  value={type}
                  label="Select Drive Type"
                  name={'type'}
                  initialOption={{
                    name: 'Select Type',
                    id: '',
                  }}
                  options={driveTypeOptions}
                  onChange={handleChange}
                  error={formError?.type}
                />
              </div>
            </>
          </InputForm>
          <div className={`font-semibold text-[#176b87] border-b pb-1 `}>
            External Storage
          </div>
          <div className="w-full grid grid-cols-1 gap-1 xl:grid-cols-4 sm:grid-cols-2  sm:gap-2 mt-3 ">
            {storage
              ?.filter((item: any) => item.type === DriveType.External)
              ?.map((drive: any, index: number) => {
                return (
                  <StorageBar
                    key={index}
                    lable={drive.code}
                    storage={
                      (drive.memoryUsed / (1024 * 1024 * 1024)).toFixed(4) +
                      `GB free of ` +
                      (drive.totalMemory / (1024 * 1024 * 1024)).toFixed(4) +
                      `GB`
                    }
                    totalMemory={drive.totalMemory}
                    usedMemory={drive.memoryUsed}
                    onClick={() => {
                      navigate(`/serverDetails/${drive.id}`);
                    }}
                  />
                );
              })}
          </div>
        </div>

        <div className="p-4 mt-3 bg-white rounded-sm shadow-lg">
          <div className={`border-b pb-1 font-semibold text-[#176b87]`}>
            Local Storage
          </div>
          <div className="grid lg:grid-cols-3 gap-4 mt-3 ">
            {storage
              ?.filter((item: any) => item.type === DriveType.Local)
              ?.map((drive: any, index: number) => {
                return (
                  <StorageBar
                    key={index}
                    lable={drive.code}
                    storage={
                      (drive.memoryUsed / (1024 * 1024 * 1024)).toFixed(4) +
                      `GB free of ` +
                      (drive.totalMemory / (1024 * 1024 * 1024)).toFixed(4) +
                      `GB`
                    }
                    totalMemory={drive.totalMemory}
                    usedMemory={drive.memoryUsed}
                    onClick={() => {
                      navigate(`/serverDetails/${drive.id}`);
                    }}
                  />
                );
              })}
          </div>
        </div>
        <div className="p-4 mt-3 bg-white rounded-md shadow-lg">
          <div className={`font-semibold text-sky-700 border-b pb-1 `}>
            Application Logs
          </div>
          <div className="w-full grid grid-cols-2 gap-1 lg:grid-cols-6 sm:grid-cols-2 sm:gap-3 mt-3 ">
            {appLog?.map((item: any, index: number) => (
              <div
                key={index}
                className={`cursor-pointer flex items-center border justify-center space-x-3 rounded-sm shadow-sm p-2 hover:bg-sky-100 ${
                  currentDate(item.date.substring(0, 10)) &&
                  'bg-sky-200 font-semibold'
                } `}
                onClick={() => {
                  navigate(
                    `/logDetails/${item.fileName.substring(
                      4,
                      item.fileName.length - 4
                    )}`
                  );
                }}
              >
                <div>
                  <i className="fa-solid fa-file text-sky-700 "></i>
                </div>
                <div className="text-sm text-sky-900">
                  {item.date.substring(0, 10)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Server;
